/**
 * Type guard function for runtime type checking.
 *
 * Used throughout the filter module to safely validate data structures
 * from API responses or configuration.
 *
 * @example
 * ```typescript
 * const isSimpleFilterValue: Guard<SimpleFilterValue> = (value): value is SimpleFilterValue => {
 *   return typeof value === 'object' && 'key' in value && 'label' in value;
 * };
 * ```
 */
export type Guard<T> = (value: unknown) => value is T;
/* =========================
   Base
========================= */
/**
 * Base properties shared by all filter value types.
 *
 * Contains the document count (number of products) matching this filter value.
 */
export type BaseFilterValue = {
  /** Number of products matching this filter value */
  doc_count: number;
};

/* =========================
   Value types
========================= */
/**
 * Simple filter value for standard filters (color, size, vendor, tags, etc.).
 *
 * Used for most filter types where each option has a key and display label.
 *
 * @example
 * ```typescript
 * const colorFilter: SimpleFilterValue = {
 *   key: 'red',
 *   label: 'Red',
 *   doc_count: 42
 * };
 * ```
 */
export type SimpleFilterValue = BaseFilterValue & {
  /** Unique identifier for the filter value */
  key: string;
  /** Display label shown to users */
  label: string;
};

/**
 * Collection filter value with extended metadata.
 *
 * Used specifically for collection-type filters that include Shopify
 * collection data like handles, tags, images, and HTML descriptions.
 *
 * @example
 * ```typescript
 * const collectionValue: CollectionFilterValue = {
 *   key: 'summer-collection',
 *   label: 'Summer Collection',
 *   displayName: 'Summer 2024',
 *   handle: 'summer-2024',
 *   tags: ['seasonal', 'new'],
 *   sort_order: 'best-selling',
 *   body_html: '<p>Summer collection description</p>',
 *   image: 'https://cdn.shopify.com/...',
 *   isDisabled: false,
 *   doc_count: 156
 * };
 * ```
 */
export type CollectionFilterValue = BaseFilterValue & {
  /** Collection identifier */
  key: string;
  /** Display label */
  label: string;
  /** Full display name for the collection */
  displayName: string;
  /** Shopify collection handle */
  handle: string;
  /** Collection tags for categorization */
  tags: string[];
  /** Sort order for products in collection */
  sort_order: string;
  /** HTML description content */
  body_html: string | null;
  /** Collection image URL */
  image: string | null;
  /** Whether this collection option is disabled */
  isDisabled: boolean;
};

/**
 * Rating filter value for star rating filters.
 *
 * Represents a rating range (e.g., "4 stars and up") with numeric bounds.
 * Typically used with review integrations like Reviews.io.
 *
 * @example
 * ```typescript
 * const fourStarsAndUp: RatingFilterValue = {
 *   key: 4,
 *   from: 4,
 *   to: 5,
 *   label: '4 stars & up',
 *   doc_count: 87
 * };
 * ```
 */
export type RatingFilterValue = BaseFilterValue & {
  /** Numeric key representing the rating */
  key: number;
  /** Minimum rating in range (inclusive) */
  from: number;
  /** Maximum rating in range (inclusive) */
  to: number;
  /** Display label for the rating range */
  label: string;
};

/* =========================
   Tag (multi-level)
========================= */
/**
 * Metadata for tag filter option list actions.
 *
 * Defines behavior when clicking on a tag filter option, including
 * filter type, selection mode, and display format.
 */
export type TagMetaActionOption = {
  /** Option key identifier */
  key: string;
  /** Option value */
  value: string;
  /** Display label */
  label: string;
  /** Type of filter (e.g., 'tag', 'vendor', 'collection') */
  filterType: string;
  /** Whether single or multiple values can be selected */
  selectType: 'single' | 'multiple';
  /** Display format for the value */
  valueDisplay: string;
};

/**
 * Metadata containing action definitions for tag filter interactions.
 *
 * Supports both option list actions and multi-level expand/collapse behavior.
 */
export type TagMetaData = {
  action: {
    /** Action triggered when selecting an option from the list */
    optionList?: TagMetaActionOption;
    /** Action for expanding/collapsing multi-level tag hierarchies */
    expandCollapseMultiLevel?: {
      /** Unique identifier for this action */
      actionId: string;
      /** Array of DOM elements to toggle when action is triggered */
      targets: {
        /** Target element ID */
        targetId: string;
        /** CSS class to toggle */
        classToggle: string;
        /** Whether to add (true) or remove (false) the class */
        shouldAddClass: boolean;
      }[];
    };
  };
};

/**
 * Tag filter value supporting hierarchical multi-level tag structures.
 *
 * Used for nested tag filters where tags can have sub-tags (children).
 * Includes action IDs and metadata for interactive expand/collapse behavior.
 *
 * @remarks
 * This type is recursive - subTags is an array of TagFilterValue, allowing
 * unlimited nesting depth for complex tag hierarchies.
 *
 * @example
 * ```typescript
 * const categoryTag: TagFilterValue = {
 *   tag: 'clothing',
 *   displayName: 'Clothing',
 *   doc_count: 250,
 *   subTags: [
 *     {
 *       tag: 'shirts',
 *       displayName: 'Shirts',
 *       doc_count: 89,
 *       subTags: []
 *     },
 *     {
 *       tag: 'pants',
 *       displayName: 'Pants',
 *       doc_count: 72,
 *       subTags: []
 *     }
 *   ],
 *   actionId: 'expand-clothing',
 *   targetId: 'clothing-subtags',
 *   className: 'is-collapsed'
 * };
 * ```
 */
export type TagFilterValue = BaseFilterValue & {
  /** Tag identifier */
  tag: string;
  /** Display name for the tag */
  displayName: string;
  /** Array of child tags in the hierarchy */
  subTags: TagFilterValue[];

  /** Action ID for the main tag element */
  actionId?: string;
  /** Action ID for the expand/collapse arrow */
  actionIdArrow?: string;

  /** Target DOM element ID for the tag */
  targetId?: string;
  /** Target DOM element ID for the option list */
  targetIdList?: string;
  /** Target DOM element ID for the arrow */
  targetIdArrow?: string;

  /** Metadata defining actions for the tag */
  metaData?: TagMetaData;
  /** Metadata defining actions for the arrow */
  metaDataArrow?: TagMetaData;

  /** CSS class name applied to the tag element */
  className?: string;
};

/* =========================
   Filter base
========================= */
/**
 * Base configuration properties shared by all filter types.
 *
 * Contains display settings, behavior flags, and styling options that apply
 * to every filter regardless of its value type (simple, collection, rating, tags).
 *
 * @remarks
 * This type is used as the base for the FilterConfig discriminated union.
 * Technical Support teams can reference these properties when customizing
 * filter behavior per shop.
 */
export type FilterBase = {
  filterType: string;
  label: string;

  isCollapseMobile: boolean;
  isCollapsePC?: boolean;
  tooltip: string | null;

  displayAllValuesInUppercaseForm?: boolean;

  showSearchBoxFilterMobile: boolean;
  showSearchBoxFilterPC: boolean;

  showMoreType: 'none' | 'auto' | 'manual';
  sortManualValues: boolean;
  sortType: string;

  filterOptionId?: string;
  position?: number;

  selectType?: 'single' | 'multiple';
  status?: 'active' | 'inactive' | 'disabled';

  valueType?: 'simple' | 'collection' | 'rating' | 'tags';

  reviewsioAttrValue?: string;

  swatchStyle?: string;
  displayType?: 'list' | 'grid';

  manualValues?: string[];
  removeTextFilterValues?: string;
  replaceTextFilterValues?: string | null;

  excludedValues?: string[];
  moneyFormatValue?: string;
};

/* =========================
   Discriminated union
========================= */
/**
 * Complete filter configuration using discriminated union based on valueType.
 *
 * This type ensures type safety when working with different filter value types.
 * Use the valueType property to narrow the type and access the correctly-typed values array.
 *
 * @remarks
 * Technical Support teams can extend filter configurations by checking the valueType
 * and applying shop-specific transformations to the values.
 *
 * @example
 * Type-safe filter value processing:
 * ```typescript
 * function processFilterConfig(config: FilterConfig) {
 *   if (config.valueType === 'collection') {
 *     // TypeScript knows config.values is CollectionFilterValue[]
 *     config.values.forEach(val => console.log(val.handle));
 *   } else if (config.valueType === 'rating') {
 *     // TypeScript knows config.values is RatingFilterValue[]
 *     config.values.forEach(val => console.log(val.from, val.to));
 *   }
 * }
 * ```
 *
 * @example
 * Extend FilterAPI to customize collection filters:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
 *   return class extends FilterAPI {
 *     protected applyFilterSettings(filter) {
 *       const result = super.applyFilterSettings(filter);
 *       result.options.forEach(option => {
 *         if (option.valueType === 'collection') {
 *           // Add custom logic for collection filters
 *           option.values = option.values.filter(v => !v.isDisabled);
 *         }
 *       });
 *       return result;
 *     }
 *   };
 * });
 * ```
 */
export type FilterConfig =
  | (FilterBase & {
      valueType?: 'simple';
      values: SimpleFilterValue[];
    })
  | (FilterBase & {
      valueType: 'collection';
      values: CollectionFilterValue[];
    })
  | (FilterBase & {
      valueType: 'rating';
      values: RatingFilterValue[];
    })
  | (FilterBase & {
      valueType: 'tags';
      values: TagFilterValue[];
    });

/**
 * Container for all filter options returned by the filter API.
 *
 * This is the main data structure received from the Boost filter engine,
 * containing all available filters and their current values.
 *
 * @example
 * ```typescript
 * const filterOptions: FilterOptionsProps = {
 *   filterId: 'collection-summer-2024',
 *   options: [
 *     {
 *       filterType: 'price',
 *       label: 'Price',
 *       valueType: 'simple',
 *       values: [{ key: '0-50', label: 'Under $50', doc_count: 42 }]
 *     }
 *   ]
 * };
 * ```
 */
export type FilterOptionsProps = {
  /** Unique identifier for this filter set (typically collection ID) */
  filterId: string;
  /** Array of all available filter configurations */
  options: FilterConfig[];
};

type Facet = {
  loaded: number;
  total?: number;
  displayType: string;
};

type FilterFacets = Record<string, Facet>;

type Color = string;
type TextTransform = 'none' | 'capitalize' | 'uppercase';
type FontWeight = string;
type FontFamily = string;
type Type = 'based_on_data' | 'manually' | 'suggestions' | 'collections' | 'products' | 'pages';
type Status = 'active' | 'inactive' | boolean;
type Position = string; // Simplified for remaining uses (e.g., suggestion positions)
type EmailProvider = 'boost';

interface WatermarkSettings {
  watermark: boolean;
}

interface CustomizeEmail {
  name: string;
  address: string;
  subject: string;
  heading: string;
  description: string;
  button: string;
  contentTextColor: Color;
  backgroundColor: Color;
  ctaTextColor: Color;
  ctaBackgroundColor: Color;
}

interface HtmlTemplate {
  content: string;
}

interface ShippingUpdateEmail {
  subject: string;
  heading: string;
  description: string;
}

interface NotificationSettings {
  emailProvider: EmailProvider;
  automatic: boolean;
  customizeEmail: CustomizeEmail;
  htmlTemplate: HtmlTemplate;
  shippingUpdateEmail: ShippingUpdateEmail;
}

interface PreOrderSettings {
  notificationSettings: NotificationSettings;
}

interface GeneralSettings {
  addCollectionToProductUrl: boolean;
  breakpointmobile: string;
  breakpointtabletportraitmin: string;
  breakpointtabletportraitmax: string;
  breakpointtabletlandscapetmin: string;
  breakpointtabletlandscapemax: string;
  breakpointdesktop: string;
}

interface SearchTermSuggestions {
  label: string;
  searchTermList: string[];
  backup: string[];
  type: Type;
  active: boolean;
}

interface MostPopularProducts {
  label: string;
  productList: any[];
  backup: any[];
  type: Type;
  active: boolean;
}

interface ProductsBlock {
  label: string;
  pageSize: number;
  active: boolean;
  displayImage: boolean;
}

interface CollectionsBlock {
  label: string;
  pageSize: number;
  active: boolean;
  displayImage: boolean;
  displayDescription: boolean;
}

interface PagesBlock {
  label: string;
  pageSize: number;
  active: boolean;
  displayImage: boolean;
  displayExcerpt: boolean;
}

interface SearchEmptyResultMessages {
  active: boolean;
  label: string;
}

interface SearchTips {
  label: string;
  active: boolean;
  searchTips: string;
}

interface SearchPanelBlocks {
  searchTermSuggestions: SearchTermSuggestions;
  mostPopularProducts: MostPopularProducts;
  products: ProductsBlock;
  collections: CollectionsBlock;
  pages: PagesBlock;
  searchEmptyResultMessages: SearchEmptyResultMessages;
  searchTips: SearchTips;
}

interface SuggestionBlockItem {
  label: string;
  status: boolean;
  data: any[];
  type: Type;
  backup: any[];
}

interface SuggestionNoResult {
  search_terms: SuggestionBlockItem;
  products: SuggestionBlockItem;
}

interface SuggestionBlock {
  type: Type;
  label: string;
  status: Status;
  number: number | string;
}

interface RecentSearch {
  label: string;
  status: boolean;
  number: string;
}

interface SearchTermSuggestion {
  label: string;
  status: boolean;
  data: string[];
  backup: string[];
  type: Type;
}

interface ProductSuggestion {
  label: string;
  status: boolean;
  data: any[];
  backup: any[];
  type: Type;
}

interface SearchBoxOnclick {
  recentSearch: RecentSearch;
  searchTermSuggestion: SearchTermSuggestion;
  productSuggestion: ProductSuggestion;
}

interface SearchSettings {
  searchPanelBlocks: SearchPanelBlocks;
  suggestionNoResult: SuggestionNoResult;
  enableInstantSearch: boolean;
  productAvailable: boolean;
  showSuggestionProductImage: boolean;
  showSuggestionProductPrice: boolean;
  showSuggestionProductSalePrice: boolean;
  showSuggestionProductSku: boolean;
  showSuggestionProductVendor: boolean;
  suggestionBlocks: SuggestionBlock[];
  searchBoxOnclick: SearchBoxOnclick;
  suggestionStyle: string;
  suggestionStyle1ProductItemType: string;
  suggestionStyle1ProductPosition: Position;
  suggestionStyle1ProductPerRow: string;
  suggestionStyle2ProductItemType: string;
  suggestionStyle2ProductPosition: Position;
  suggestionStyle2ProductPerRow: string;
  suggestionStyle3ProductItemType: string;
  suggestionStyle3ProductPosition: Position;
  suggestionStyle3ProductPerRow: string;
}

interface FilterStyle {
  filterTitleTextColor: Color;
  filterTitleFontSize: string;
  filterTitleFontWeight: FontWeight;
  filterTitleFontTransform: TextTransform;
  filterTitleFontFamily: FontFamily;
  filterOptionTextColor: Color;
  filterOptionFontSize: string;
  filterOptionFontFamily: FontFamily;
}

interface FilterSettings {
  showFilterOptionCount: boolean;
  showRefineBy: boolean;
  showOutOfStockOption: boolean;
  showSingleOption: boolean;
  keepToggleState: boolean;
  changeMobileButtonLabel: boolean;
  sortingAvailableFirst: boolean;
  showLoading: boolean;
  activeScrollToTop: boolean;
  showVariantImageBasedOnSelectedFilter: string;
  productAndVariantAvailable: boolean;
  availableAfterFiltering: boolean;
  isShortenUrlParam: boolean;
  filterTreeMobileStyle: string;
  filterTreeVerticalStyle: string;
  filterTreeHorizontalStyle: string;
  stickyFilterOnDesktop: boolean;
  stickyFilterOnMobile: boolean;
  style: FilterStyle;
  filterLayout: string;
}

interface AdditionalElementSettings {
  customSortingList: string;
  enableCollectionSearch: boolean;
}

interface ThemeSettings {
  // Using 'any' to simplify due to complexity/length
  [key: string]: any;
}

interface Reviewsio {
  reRanking: boolean;
  connected: boolean;
  firstConnectedAt: number;
  lastUpdatedAt: number;
}

interface ExtraFeatures {
  reviewsio: Reviewsio;
}

interface CurrentAppIntegration {
  extraFeatures: ExtraFeatures;
  labels: string;
  reviews: string;
}

interface WidgetInfo {
  appPreferenceSettings: WatermarkSettings;
  preOrderSettings: PreOrderSettings;
  generalSettings: GeneralSettings;
  searchSettings: SearchSettings;
  filterSettings: FilterSettings;
  additionalElementSettings: AdditionalElementSettings;
  themeSettings: ThemeSettings;
  currentAppIntegration: CurrentAppIntegration;
  themeName: string;
  srcThemeNameLib: string;
}

/**
 * Initial properties for filter widget initialization.
 *
 * These properties are passed when creating a new filter widget instance,
 * containing configuration, DOM context, and initial data.
 *
 * @remarks
 * This type extends into FilterStates which adds runtime state management properties.
 * Technical Support teams typically don't modify these directly but may need to
 * reference them when extending filter initialization logic.
 */
export type FilterProps = {
  /** Unique identifier for this filter widget instance */
  id: string;
  /** Type of block ('filter', 'collection', 'search') */
  blockType: string;
  /** Cache storage for API responses */
  cache: Record<string, object>;
  /** Default URL parameters for filter requests */
  defaultParams: Record<string, any>;
  /** DOM context (document or container element) */
  document: HTMLElement | Document;
  /** Dynamic bundle configurations for product bundles */
  dynamicBundles: Array<unknown>;
  /** Current filter options and values */
  filter: FilterOptionsProps;
  /** Widget identifier from app configuration */
  widgetId: string;
  /** Default sorting option */
  defaultSort: string;
  /** Viewport configuration for filter display */
  viewPort: FilterFacets;
  /** Complete widget and theme settings */
  templateSettings: WidgetInfo;
};

export type ViewMoreState = {
  label: string;
  values: any[];
  numberViewMore: number;
  enabled: boolean;
  isExpanded: boolean;
  showMoreType: string;
  dom: HTMLElement;
  viewMoreBtnDom: HTMLElement | null;
  option: any;
  swatchShape?: string;
  swatchType?: string;
  showDocCount?: boolean;
  selectedKeys: Record<string, boolean>;
  classNameSelected: string;
  classNameUnselected: string;
  lastClickedTime: number;
};

/**
 * Complete runtime state for an active filter widget.
 *
 * Extends FilterProps with all runtime state management properties including
 * loading states, user interactions, URL parameter mappings, and UI state.
 *
 * @remarks
 * This is the core state object used throughout the filter module. It's stored
 * in FilterStore and accessed by FilterAPI, FilterHandler, and FilterHelper.
 *
 * Technical Support teams can reference this type when extending filter behavior
 * to understand what state properties are available for customization.
 *
 * @example
 * Access filter state in custom handler:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
 *   return class extends FilterHandler {
 *     async handleSort(e, actionId) {
 *       const state = this.filterStore.getState();
 *       // Access state properties
 *       if (state.isLoading) return;
 *
 *       // Custom logic before sorting
 *       console.log('Current page:', state.pagination.page);
 *       console.log('Selected filters:', state.selectedFilter);
 *
 *       return super.handleSort(e, actionId);
 *     }
 *   };
 * });
 * ```
 */
export type FilterStates = FilterProps & {
  isLoading: boolean;
  error: Error | null;
  latestRequestKey: string | null;
  shortParamsMap: Map<string, string>;
  longParamsMap: Map<string, string>;
  optionsMap: Record<string, FilterConfig>;
  pagination: { page: number };
  filterTreeDesktopOpening: boolean;
  infiniteScrollLoading: boolean;
  collapseState: Record<string, any>;
  filterKeyCode: string;
  filterOptionItemSelecting: Record<string, any>;
  selectedFilter: any;
  forceRender: boolean;
  paginationInfiniteLoading: boolean;
  actionMapping: Record<string, Record<string, any>>;
  viewMoreState?: Record<string, ViewMoreState>;
  viewMoreTrigger?: {
    filterOptionId: string;
    displayType: string;
  };
  // Collection state
  collectionId?: string | null;
  collectionHandle?: string | null;
  collectionTags?: string[] | null;
  // Multi-level collection collapse state
  collapseStateMultipleLevel?: Record<string, any>;
  filterCurrentFocus: string;
  filterOptionIdMobileSelected: any;
  previousFilterOptions: any;
  filterResetTimeoutId: any;
  filterTreeViewPort: any;
  rangeFilter: any;
  sort: any;
  isRendering: boolean;
  oldDOM: HTMLElement | null;
  stickyOffset: any;
  limitList: any;
  processedLabels: WeakSet<HTMLElement>;
  activeViewAsButton: any;
  isClearing?: boolean;
  filterKeyboardActive?: boolean;
};

/* =========================
   API Types
========================= */

/**
 * URL parameters sent to the Boost filter API.
 *
 * Key-value pairs representing filter selections, sorting, pagination, and other
 * query parameters. Values can be arrays for multi-select filters or primitives
 * for single-select options.
 *
 * @example
 * ```typescript
 * const params: FilterAPIParams = {
 *   pf_st_color: ['red', 'blue'],  // Multi-select color filter
 *   pf_st_size: 'large',           // Single-select size
 *   sort_by: 'price-ascending',
 *   page: 2,
 *   limit: 24
 * };
 * ```
 */
export type FilterAPIParams = {
  [key: string]: any[] | string | number | boolean | undefined;
};

/**
 * Additional options for customizing filter API requests.
 *
 * Provides hooks for Technical Support teams to modify request parameters,
 * control response format, and specify pagination behavior.
 *
 * @remarks
 * The modifyParams callback is particularly useful for adding shop-specific
 * parameters or transforming filters before sending to the API.
 *
 * @example
 * Add custom parameters to every filter request:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterAPI', (FilterAPI) => {
 *   return class extends FilterAPI {
 *     async getProductByFilter(params, additions) {
 *       const customAdditions = {
 *         ...additions,
 *         modifyParams: (params) => ({
 *           ...params,
 *           shop_id: this.getShopId(),
 *           customer_tag: this.getCustomerTag()
 *         })
 *       };
 *       return super.getProductByFilter(params, customAdditions);
 *     }
 *   };
 * });
 * ```
 */
export type FilterAPIAdditionParams = {
  /** Response format - 'json' for data only, 'html' for rendered HTML */
  responseType?: 'json' | 'html';
  /** Callback to transform parameters before sending request */
  modifyParams?: ((params: FilterAPIParams) => FilterAPIParams) | null;
  /** Additional parameters to merge into the request */
  additionParams?: Record<string, unknown>;
  /** Pagination behavior: 'more' appends, 'previous' prepends, 'replace' replaces */
  behavior?: 'more' | 'previous' | 'replace';
  /** Whether currently focused on in-collection search */
  inCollectionSearchFocusing?: boolean;
  /** Widget ID for analytics tracking */
  widgetId?: string;
};

/**
 * Response data from the Boost filter API.
 *
 * Contains rendered HTML, updated filter options, dynamic bundles,
 * and metadata including currency formatting and request tracking.
 *
 * @remarks
 * The html property contains pre-rendered product HTML ready to inject into the DOM.
 * The filter property contains updated filter options reflecting current selections.
 *
 * @example
 * ```typescript
 * const response: FilterAPIResponse = {
 *   html: '<div class="product-item">...</div>',
 *   filter: { filterId: 'col-123', options: [...] },
 *   bundles: [],
 *   request_id: 'req-abc-123',
 *   meta: {
 *     rid: 'rid-xyz-789',
 *     money_format: '${{amount}}',
 *     currency: 'USD'
 *   }
 * };
 * ```
 */
export type FilterAPIResponse = {
  /** Rendered HTML for product items */
  html: string;
  /** Updated filter options based on current selection */
  filter: FilterOptionsProps;
  /** Dynamic bundle configurations */
  bundles: unknown[];
  /** Request identifier for tracking */
  request_id?: string;
  /** Metadata including currency and formatting */
  meta?: {
    /** Request ID (alternative key) */
    rid?: string;
    /** Shopify money format pattern */
    money_format?: string;
    /** Money format with currency symbol */
    money_format_with_currency?: string;
    /** Currency code (USD, EUR, etc.) */
    currency?: string;
  };
};

/**
 * Cached API response with timestamp.
 *
 * Used by FilterAPI to cache responses and avoid redundant network requests.
 * Technical Support teams can extend FilterAPI to customize cache invalidation logic.
 *
 * @example
 * ```typescript
 * const cacheEntry: CacheEntry = {
 *   html: '<div>...</div>',
 *   filter: { filterId: 'col-123', options: [] },
 *   bundles: [],
 *   timestamp: Date.now()
 * };
 * ```
 */
export type CacheEntry = {
  /** Cached HTML response */
  html: string;
  /** Cached filter options */
  filter: FilterOptionsProps;
  /** Cached bundles */
  bundles: unknown[];
  /** Cache timestamp for TTL calculations */
  timestamp: number;
};

/**
 * UI state to preserve during DOM updates
 */
export type FilterTreeState = {
  isMobileTreeOpen: boolean;
  expandedFilterId: string | null;
  scrollPosition: number;
  mobileFilterContent?: string;
};

/* =========================
   Product Price Types
========================= */

/**
 * Product data structure from data-product attribute
 */
export interface ProductData {
  priceMin: number | string;
  priceMax: number | string;
  compareAtPriceMin: number | string;
  compareAtPriceMax: number | string;
  handle?: string;
  variants?: unknown[];
  [key: string]: unknown;
}

/**
 * Price display settings from theme configuration
 */
export interface PriceDisplaySettings {
  showCentAsSuperscript?: boolean;
  showCurrencyCodes?: boolean;
  compareAtPricePosition?: 'before' | 'after';
  showSavingDisplay?: boolean;
}

/**
 * Price transformation context with DOM and settings
 */
export interface PriceTransformContext {
  document: Document | HTMLElement;
  blockType?: 'filter' | 'recommendation' | string;
  templateSettings?: {
    themeSettings?: {
      productItems?: {
        productInfo?: {
          textAlign?: string;
          elements?: {
            price?: PriceDisplaySettings;
          };
        };
      };
    };
  };
  app?: {
    translation?: {
      productItem?: {
        amount?: string;
        savingAmount?: string;
      };
    };
  };
}

/**
 * Parameters for price HTML generation
 */
export interface PriceRenderParams extends PriceDisplaySettings {
  textAlign?: string;
  productItemSavingAmountTranslation?: string;
  productItemAmountTranslation?: string;
}

/**
 * Function type for rendering price HTML
 */
export type PriceRenderer = (
  priceMin: number | string,
  compareAtPriceMin: number | string,
  priceMax: number | string | null,
  params: PriceRenderParams
) => Promise<string>;

/**
 * Valid event name - either string literal or typed EVENT_NAMES key.
 */
export type EventName = string | keyof typeof EVENT_NAMES;

/**
 * Event callback function signature.
 *
 * @param data - Optional event payload data
 */
export type Callback<T = unknown> = (data?: T) => void;

/**
 * Map storing event subscriptions: event name -> array of callbacks.
 */
export type EventsMap = Map<EventName, Callback[]>;

/**
 * Pair of event name and callback for bulk subscription operations.
 */
export type EventCallbackPair = { event: EventName; callback: Callback };

/**
 * Standard event names used throughout the filter widget.
 *
 * These constants define all available events for filter state changes,
 * user interactions, and UI updates. Technical Support teams can listen
 * to these events to hook into filter behavior.
 *
 * @remarks
 * Events follow the pattern `${action}_${trigger}` or `${action}_changed`.
 * Use FilterStore.on() to subscribe to these events.
 *
 * @example
 * Listen to filter changes:
 * ```javascript
 * const filterStore = moduleRef.get('FilterStore');
 * filterStore.on(EVENT_NAMES.FILTER_CHANGED, (data) => {
 *   console.log('Filter selection changed:', data);
 *   // Custom analytics tracking
 *   trackFilterEvent(data);
 * });
 * ```
 */
export const EVENT_NAMES = {
  COLLAPSE_CHANGED: 'collapse_changed',
  FILTER_CHANGED: 'filter_changed',
  SORT_CHANGED: 'sort_changed',
  FORCE_RENDER_CHANGED: 'force_render_changed',
  FILTER_OPTION_RENDERED: 'filter_option_rendered',
  VIEWMORE_TRIGGERED: 'viewmore_triggered',
  FILTER_SELECTED: 'filter_selected',
  AFTER_DOCUMENT_CHANGED: 'document_changed',
  NEW_DOCUMENT_CHANGED: 'new_document_changed',
  FILTER_RESET: 'filter_reset',

  // Handler events from action-on-click.js
  OPTION_LIST: 'option_list',
  CLEAR_ALL_FILTER: 'clear_all_filter',
  CLEAR_FILTER: 'clear_filter',
  REMOVE_FILTER_FROM_REFINE_BY: 'remove_filter_from_refine_by',
  ADD_TO_CART: 'add_to_cart',
  QUICK_VIEW: 'quick_view',
  OPTION_COLLECTION: 'option_collection',
  FILTER_OPTION_COLLAPSE: 'filter_option_collapse',
  QUICK_ADD_TO_CART: 'quick_add_to_cart',
  APPLY_FILTER_OPTION: 'apply_filter_option',
  RESET_FILTER_OPTION: 'reset_filter_option',
  EXPAND_COLLAPSE_MULTI_LEVEL: 'expand_collapse_multi_level',
  GO_TO_PRODUCT_DETAIL: 'go_to_product_detail',
  HANDLE_SORT: 'handle_sort',
  PAGINATION: 'pagination',
  HANDLE_TOGGLE_SHOW_LIMIT_LIST: 'handle_toggle_show_limit_list',
  LIMIT_LIST_CHANGED: 'limit_list_changed',
  HANDLE_LIMIT_LIST_OPTION_CLICK: 'handle_limit_list_option_click',
  HANDLE_VIEW_AS_CLICK: 'handle_view_as_click',
  SHOW_MOBILE_FILTER_TREE: 'show_mobile_filter_tree',
  TOGGLE_FILTER_TREE_BUTTON_DESKTOP: 'toggle_filter_tree_button_desktop',
  CLOSE_MOBILE_FILTER_TREE: 'close_mobile_filter_tree',
  BACK_FILTER_TREE_MOBILE: 'back_filter_tree_mobile',
  CLOSE_QUICK_ADD_TO_CART: 'close_quick_add_to_cart',
  SCROLL_TO_TOP: 'scroll_to_top',
  HANDLE_CLICK_EMBEDDED_BUNDLE: 'handle_click_embedded_bundle',
  HANDLE_CLICK_VOLUME_BUNDLE: 'handle_click_volume_bundle',
  HANDLE_SHOW_RESULT: 'handle_show_result',
  RENDER_REFINE_BY: 'render_refine_by',
  LOAD_STICKY_HORIZONTAL_DESKTOP: 'load_sticky_horizontal_desktop',
  LOAD_STICKY_VERTICAL_DESKTOP: 'load_sticky_vertical_desktop',
  LOAD_STICKY_MOBILE: 'load_sticky_mobile',
  HANDLE_OUTSIDE_SORT_CLICK: 'handle_outside_sort_click',
  HANDLE_OUTSIDE_LIMIT_LIST_CLICK: 'handle_outside_limit_list_click',
  HANDLE_OUTSIDE_MINI_POPUP_CLICK: 'handle_outside_mini_popup_click',
  DEBOUNCE_INCOLLECTION_SEARCH: 'debounce_incollection_search',
} as const;

/**
 * Base payload structure for filter events.
 *
 * Contains the event key, new value, and optionally the previous value
 * for tracking state transitions.
 *
 * @remarks
 * Specific event types may extend this with additional properties.
 */
export interface EventPayload {
  /** The event that was triggered */
  key: keyof typeof EVENT_NAMES;
  /** New value after the change */
  newValue: unknown;
  /** Previous value before the change (optional) */
  oldValue?: unknown;
}

/**
 * Typed event handler that receives both custom data and base EventPayload.
 *
 * @example
 * ```typescript
 * const handler: EventHandler<{ filterId: string }> = (payload) => {
 *   console.log('Event:', payload.key);
 *   console.log('Filter ID:', payload.filterId);
 *   console.log('New value:', payload.newValue);
 * };
 * ```
 */
export type EventHandler<T = unknown> = (payload: T & EventPayload) => void;

export type Primitive = string | number | boolean | null | undefined;

/**
 * Shared context object passed between range slider helper methods.
 *
 * Collects all DOM references, computed settings, and mutable slider state
 * so that each helper function has a single, explicit parameter instead of
 * relying on shared closure variables.
 */
export interface RangeSliderContext {
  min: number;
  max: number;
  valueRange: number;
  sliderStep: number;
  option: any;
  sliderOption: any;
  rangeFilter: Record<string, any>;
  filterState: any[];
  isFilterVertical: boolean;
  horizontalHasApplyButton: boolean;
  removeDecimal: boolean;
  slider: Element;
  sliderBase: HTMLElement;
  minHandle: HTMLElement;
  maxHandle: HTMLElement;
  inputLower: HTMLInputElement | null;
  inputUpper: HTMLInputElement | null;
}
